/**
 * TicketComposerPane — the reply/forward composer (full-screen-capable).
 * Self-contained UI block extracted from TicketsInboxPage. Receives all state
 * and handlers from the parent.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronRight,
  Forward,
  Maximize2,
  Minimize2,
  Paperclip,
  Pencil,
  RefreshCw,
  Reply,
  Sparkles,
  StickyNote,
  UserRound,
  X,
  Loader2,
} from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { sanitizeAttachmentHtml } from './TicketComponents';
import { sanitizeHtml } from '@/lib/sanitize';
import { BACKEND_BASE_URL } from '@/lib/apiBase';

export function TicketComposerPane({
  selectedTicket,
  composerFullscreen,
  setComposerFullscreen,
  replyText,
  setReplyText,
  replying,
  onSendReply,
  onUpdateStatus,
  onAssignTicket,
  assignees = [],
  composeTo,
  setComposeTo,
  composeCc,
  setComposeCc,
  composeBcc,
  setComposeBcc,
  showRecipientsExpanded,
  setShowRecipientsExpanded,
  showBcc,
  setShowBcc,
  emailContacts = [],
  editingSubject,
  setEditingSubject,
  draftSubject,
  setDraftSubject,
  replyAttachments = [],
  onPasteImage,
  onPickAttachments,
  onRemoveAttachment,
  replyKind,
  setReplyKind,
  statusAfterSend,
  setStatusAfterSend,
  replyMode,
  onClose,
  isStaff,
  quotedThreadHtml,
  replySnippets = [],
  onInsertSnippet,
  aiBusy = false,
  onAiSuggest,
  onUpdateFollowers,
  senderIdentities = [],
  senderIdentity = '',
  setSenderIdentity,
  replyAsUserId = '',
  setReplyAsUserId,
  currentUserId = '',
}) {
  const staff = typeof isStaff === 'function' ? isStaff() : !!isStaff;

  // Notes (public/private) are internal — hide all email chrome for them.
  const isNote = replyKind === 'public_note' || replyKind === 'private_note';

  if (!selectedTicket) return null;

  return (
    <div
      className={`flex flex-col overflow-hidden min-w-0 ${
        composerFullscreen ? 'fixed inset-0 z-50 bg-white' : 'flex-1 border-l border-slate-200'
      }`}
      data-testid="composer-pane"
      onKeyDown={(e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          if (!replying && replyText.trim()) {
            e.preventDefault();
            onSendReply();
          }
        }
      }}
    >
      <div className="bg-white flex flex-col h-full">
        {/* Composer Header */}
        <div className="border-b border-slate-200 bg-white px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <Badge variant="outline" className="bg-slate-900 text-white border-slate-900 font-mono text-xs">
              #{selectedTicket.ticket_number}
            </Badge>
            <div className="w-36 sm:w-40">
              <Select value={selectedTicket.status} onValueChange={onUpdateStatus}>
                <SelectTrigger className="h-8 text-xs" data-testid="composer-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                  <SelectItem value="under_investigation">Under Investigation</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" title="Assign">
                  <UserRound className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="start">
                <div className="text-xs font-medium text-slate-600 mb-2">Assign ticket</div>
                <div className="space-y-1 max-h-64 overflow-auto">
                  <Button type="button" variant="ghost" className="w-full justify-start" onClick={() => onAssignTicket('')}>
                    Unassigned
                  </Button>
                  {assignees.map((a) => (
                    <Button
                      key={a.id}
                      type="button"
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => onAssignTicket(a.id)}
                    >
                      {a.name || a.email}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setComposerFullscreen(!composerFullscreen)}
              title={composerFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              data-testid="composer-fullscreen-toggle"
            >
              {composerFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>
              {composerFullscreen ? 'Close' : 'Back to ticket'}
            </Button>
          </div>
        </div>

        <div className={`flex-1 overflow-auto bg-slate-50 ${composerFullscreen ? 'p-8' : 'p-4'}`} data-testid="ticket-composer-only">
          <div className={`rounded-xl border ${isNote ? (replyKind === 'private_note' ? 'border-slate-300 bg-slate-50 border-dashed' : 'border-amber-300 bg-amber-50') : 'border-slate-200 bg-white'} shadow-sm overflow-hidden ${composerFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
            {isNote ? (
              /* Note header — no email fields, no thread. Just a colored pill. */
              <div className="px-5 py-3 border-b border-slate-200/60 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                      replyKind === 'private_note'
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-amber-500 text-white border-amber-500'
                    }`}
                    data-testid={`note-mode-label-${replyKind}`}
                  >
                    {replyKind === 'private_note' ? 'Private note' : 'Public note'}
                  </span>
                  <span className="text-xs text-slate-500">
                    {replyKind === 'private_note'
                      ? 'Staff-only, no email sent.'
                      : 'Customer can see it, no email sent.'}
                  </span>
                </div>
                <div className="text-xs text-slate-400">
                  #{selectedTicket.ticket_number}
                </div>
              </div>
            ) : (
              <>
                {/* Subject row */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Subject</div>
                    {editingSubject ? (
                      <Input
                        value={draftSubject}
                        onChange={(e) => setDraftSubject(e.target.value)}
                        onBlur={() => setEditingSubject(false)}
                        autoFocus
                        className="mt-1"
                      />
                    ) : (
                      <div className="text-sm font-semibold text-slate-900 truncate mt-0.5">{draftSubject}</div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-slate-700"
                    onClick={() => setEditingSubject(true)}
                    title="Edit subject"
                    data-testid="edit-subject"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>

                {/* Recipients pane — email replies only */}
                <div className="px-5 py-3 border-b border-slate-100 space-y-2">
                  {/* From identity picker */}
                  <div className="flex items-baseline gap-3">
                    <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium">From</div>
                    {senderIdentities.length > 1 && setSenderIdentity ? (
                      <Select value={senderIdentity || ''} onValueChange={setSenderIdentity}>
                        <SelectTrigger className="h-8 text-sm flex-1 max-w-md" data-testid="composer-from-select">
                          <SelectValue placeholder="Select sender" />
                        </SelectTrigger>
                        <SelectContent>
                          {senderIdentities.map((s) => (
                            <SelectItem key={s.email} value={s.email}>
                              {s.name ? `${s.name} <${s.email}>` : s.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm text-slate-700" data-testid="composer-from-static">
                        {senderIdentity || (senderIdentities[0] && (senderIdentities[0].name ? `${senderIdentities[0].name} <${senderIdentities[0].email}>` : senderIdentities[0].email)) || 'Support'}
                      </div>
                    )}
                  </div>

                  {/* Reply-as picker (staff-only, internal) — respond as another team member */}
                  {staff && !isNote && setReplyAsUserId && (
                    <div className="flex items-baseline gap-3">
                      <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium">As</div>
                      <Select
                        value={replyAsUserId || '__self__'}
                        onValueChange={(v) => setReplyAsUserId(v === '__self__' ? '' : v)}
                      >
                        <SelectTrigger className="h-8 text-sm flex-1 max-w-md" data-testid="composer-reply-as-select">
                          <SelectValue placeholder="Reply as…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__self__" data-testid="composer-reply-as-self">
                            Myself
                          </SelectItem>
                          {(assignees || [])
                            .filter((a) => a && a.id && a.id !== currentUserId)
                            .map((a) => (
                              <SelectItem key={a.id} value={a.id} data-testid={`composer-reply-as-${a.id}`}>
                                {a.name || a.email} {a.email ? `<${a.email}>` : ''}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
              <button
                type="button"
                onClick={() => setShowRecipientsExpanded((v) => !v)}
                className="w-full flex items-baseline gap-3 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
                data-testid="recipients-toggle"
              >
                <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium">To</div>
                <div className="text-sm text-slate-700 truncate flex-1">
                  {composeTo || <span className="text-slate-400 italic">click to add recipients</span>}
                </div>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showRecipientsExpanded ? 'rotate-90' : ''}`} />
              </button>

              {showRecipientsExpanded && (
                <div className="pt-2 space-y-2">
                  <datalist id="cc-bcc-suggestions">
                    {emailContacts.map((e) => (
                      <option key={e} value={e} />
                    ))}
                  </datalist>
                  <div className="flex items-center gap-2">
                    <Input
                      value={composeTo}
                      onChange={(e) => setComposeTo(e.target.value)}
                      placeholder="email@example.com"
                      className="h-9"
                      list="cc-bcc-suggestions"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={() => setShowBcc((v) => !v)}
                      data-testid="bcc-toggle"
                    >
                      {showBcc ? 'Hide Bcc' : 'Bcc'}
                    </Button>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Cc</div>
                    <Input
                      value={composeCc}
                      onChange={(e) => setComposeCc(e.target.value)}
                      placeholder="comma separated"
                      className="h-9 flex-1"
                      list="cc-bcc-suggestions"
                      data-testid="composer-cc-input"
                    />
                  </div>
                  {showBcc && (
                    <div className="flex items-baseline gap-3">
                      <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Bcc</div>
                      <Input
                        value={composeBcc}
                        onChange={(e) => setComposeBcc(e.target.value)}
                        placeholder="comma separated"
                        className="h-9 flex-1"
                        list="cc-bcc-suggestions"
                        data-testid="composer-bcc-input"
                      />
                    </div>
                  )}

                  {/* Followers — auto-CC'd on every reply. Add/remove inline. */}
                  {(typeof isStaff === 'function' ? isStaff() : !!isStaff) && (
                    <div className="flex items-start gap-3">
                      <div className="w-14 text-[11px] uppercase tracking-wide text-slate-400 font-medium pt-1.5">Followers</div>
                      <div className="flex-1 min-w-0 flex flex-wrap gap-1.5 items-center">
                        {(selectedTicket.followers || []).map((f) => (
                          <Badge
                            key={(f.user_id || '') + (f.email || '')}
                            variant="outline"
                            className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[11px] gap-1"
                          >
                            {f.name || f.email}
                            {onUpdateFollowers && (
                              <button
                                type="button"
                                onClick={() =>
                                  onUpdateFollowers(
                                    (selectedTicket.followers || []).filter(
                                      (x) => x !== f && !(x.user_id === f.user_id && x.email === f.email),
                                    ),
                                  )
                                }
                                className="ml-0.5 text-cyan-700/70 hover:text-cyan-900"
                                aria-label={`Remove follower ${f.email}`}
                                data-testid={`composer-follower-remove-${f.email}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                        {onUpdateFollowers && assignees.length > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                                data-testid="composer-add-follower-btn"
                              >
                                + Add follower
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-0" align="start">
                              <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                                CC on every reply
                              </div>
                              <div className="max-h-56 overflow-auto p-1">
                                {assignees.map((a) => {
                                  const already = (selectedTicket.followers || []).some(
                                    (f) => (f.user_id && f.user_id === a.id) || (f.email && f.email === a.email),
                                  );
                                  return (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => {
                                        if (already) return;
                                        onUpdateFollowers([
                                          ...(selectedTicket.followers || []),
                                          { user_id: a.id || '', name: a.name || a.email || '', email: a.email || '' },
                                        ]);
                                      }}
                                      disabled={already}
                                      className="w-full text-left px-2 py-1.5 rounded hover:bg-cyan-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                                      data-testid={`composer-add-follower-${a.id}`}
                                    >
                                      <div className="min-w-0">
                                        <div className="text-sm text-slate-700 truncate">{a.name || a.email}</div>
                                        {a.name && a.email && <div className="text-[10px] text-slate-400 truncate">{a.email}</div>}
                                      </div>
                                      {already && <span className="text-[10px] text-cyan-700">✓</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        {(selectedTicket.followers || []).length === 0 && !onUpdateFollowers && (
                          <span className="text-xs text-slate-400 italic">No followers yet</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
              </>
            )}

            {/* Message Editor */}
            <div className="px-5 py-4">
              <RichTextEditor
                content={replyText}
                onChange={(html) => setReplyText(html)}
                placeholder="Write your message. Drag & drop or paste images directly."
                minHeight={composerFullscreen ? 320 : 200}
                onPasteImage={onPasteImage}
              />
            </div>

            {/* Attachment pills */}
            {replyAttachments.length > 0 && (
              <div className="px-5 pb-3">
                <div className="flex flex-wrap gap-2">
                  {replyAttachments.map((att) => {
                    const isImg = (att.mime_type || '').startsWith('image/');
                    return (
                      <div
                        key={att.id}
                        className="group inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full pl-1 pr-2 py-1 text-xs transition-colors"
                        data-testid="composer-attachment-pill"
                      >
                        {isImg ? (
                          <img
                            src={`${BACKEND_BASE_URL}${att.url}`}
                            alt={att.filename}
                            className="w-6 h-6 object-cover rounded-full"
                          />
                        ) : (
                          <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                            <Paperclip className="w-3 h-3 text-slate-600" />
                          </span>
                        )}
                        <span className="text-slate-700 truncate max-w-[180px]" title={att.filename}>
                          {att.filename}
                        </span>
                        <button
                          type="button"
                          className="text-slate-400 hover:text-rose-600 transition-colors"
                          onClick={() => onRemoveAttachment(att.id)}
                          title="Remove attachment"
                          aria-label="Remove attachment"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action bar */}
            <div className="px-3 sm:px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2 order-2 sm:order-1 w-full sm:w-auto">
                <input
                  type="file"
                  id="composer-attachments"
                  multiple
                  accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={onPickAttachments}
                  className="hidden"
                  data-testid="composer-file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => document.getElementById('composer-attachments').click()}
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  data-testid="composer-attach-btn"
                >
                  <Paperclip className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Attach</span>
                </Button>

                {/* Reply snippets picker — staff-curated canned responses */}
                {staff && replySnippets.length > 0 && onInsertSnippet && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        data-testid="composer-snippets-btn"
                        title="Insert reply snippet"
                      >
                        <StickyNote className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Snippets</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="start">
                      <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                        Insert canned reply
                      </div>
                      <div className="max-h-72 overflow-auto p-1">
                        {replySnippets.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => onInsertSnippet(s)}
                            className="w-full text-left px-2 py-1.5 rounded hover:bg-indigo-50 text-sm text-slate-700"
                            data-testid={`composer-snippet-${s.id}`}
                          >
                            <div className="font-medium truncate">{s.title}</div>
                            <div className="text-[11px] text-slate-400 truncate">
                              {(s.body || '').replace(/<[^>]+>/g, ' ').slice(0, 90)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {/* AI suggest reply (staff only) */}
                {staff && onAiSuggest && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={aiBusy}
                        className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        data-testid="composer-ai-btn"
                        title="Draft a reply with AI"
                      >
                        {aiBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" />
                        ) : (
                          <Sparkles className="w-4 h-4 sm:mr-1.5" />
                        )}
                        <span className="hidden sm:inline">AI draft</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="start">
                      <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                        Pick a tone — we&apos;ll draft or polish your reply
                      </div>
                      <div className="p-1 grid grid-cols-2 gap-1">
                        {['professional', 'friendly', 'concise', 'apologetic'].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => onAiSuggest({ tone: t })}
                            disabled={aiBusy}
                            className="px-2 py-1.5 rounded hover:bg-violet-50 text-sm text-slate-700 capitalize text-left disabled:opacity-50"
                            data-testid={`composer-ai-tone-${t}`}
                          >
                            ✨ {t}
                          </button>
                        ))}
                      </div>
                      <div className="px-2 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                        If you&apos;ve typed something, we polish it. If the editor is empty, we draft from the ticket. Edit before sending.
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                <span className="text-[11px] text-slate-400 hidden md:inline">
                  or paste images (Ctrl+V)
                </span>
                {staff && (
                  <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5" data-testid="composer-kind-toggle">
                    <button
                      type="button"
                      onClick={() => setReplyKind('reply')}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                        replyKind === 'reply' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid="composer-kind-reply"
                      title="Send email reply to customer"
                    >
                      Reply
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplyKind('public_note')}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                        replyKind === 'public_note' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid="composer-kind-public-note"
                      title="Public note — customer can see it, no email sent"
                    >
                      <span className="sm:hidden">Public</span>
                      <span className="hidden sm:inline">Public note</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplyKind('private_note')}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                        replyKind === 'private_note' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid="composer-kind-private-note"
                      title="Private note — staff only, no email sent"
                    >
                      <span className="sm:hidden">Private</span>
                      <span className="hidden sm:inline">Private note</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 order-1 sm:order-2 ml-auto">
                {/* Optional: change status after the reply lands */}
                <Select
                  value={statusAfterSend || 'none'}
                  onValueChange={(v) => setStatusAfterSend(v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="status-after-send">
                    <SelectValue placeholder="After: keep status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">After: keep status</SelectItem>
                    <SelectItem value="open">→ Open</SelectItem>
                    <SelectItem value="in_progress">→ In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">→ Waiting on Customer</SelectItem>
                    <SelectItem value="under_investigation">→ Under Investigation</SelectItem>
                    <SelectItem value="resolved">→ Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-600">
                  Cancel
                </Button>
                <Button
                  onClick={onSendReply}
                  disabled={replying || !replyText.trim()}
                  size="sm"
                  className={`${
                    replyKind === 'private_note'
                      ? 'bg-slate-800 hover:bg-slate-900 active:bg-black'
                      : replyKind === 'public_note'
                      ? 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800'
                  } text-white shadow-md hover:shadow-lg transition-all rounded-full pl-4 pr-5 h-9 font-medium disabled:opacity-50 disabled:shadow-none`}
                  data-testid="send-reply"
                  title="Send (Ctrl+Enter)"
                >
                  {replying ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                      {replyKind === 'reply' ? 'Sending…' : 'Saving…'}
                    </>
                  ) : replyKind !== 'reply' ? (
                    <>
                      <StickyNote className="w-4 h-4 mr-1.5" />
                      {replyKind === 'private_note' ? 'Add private note' : 'Add public note'}
                    </>
                  ) : (
                    <>
                      {replyMode === 'forward' ? (
                        <Forward className="w-4 h-4 mr-1.5" />
                      ) : (
                        <Reply className="w-4 h-4 mr-1.5" />
                      )}
                      {replyMode === 'forward'
                        ? 'Forward'
                        : replyMode === 'reply_all'
                        ? 'Send reply all'
                        : 'Send reply'}
                      <span className="ml-2 hidden sm:inline text-[10px] text-indigo-200 border border-indigo-400/60 rounded px-1 py-0.5 leading-none">
                        ⌘⏎
                      </span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Thread history — only for email replies, not notes */}
            {!isNote && (
              <div className="px-5 py-4 bg-slate-50/60 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mb-2">
                  Thread
                </div>
                <div
                  className="crm-rich-content prose prose-sm prose-slate max-w-none text-slate-600"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(sanitizeAttachmentHtml(quotedThreadHtml)) || '<p class="text-slate-400 italic">No thread yet</p>' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicketComposerPane;
